import type { JSX } from 'react'
import { Link } from 'react-router'
import { useBrand } from '@/brand/context'
import { SiteChip } from '@/settings/SiteChip'
import { TargetSetup } from '@/settings/publish-target/TargetSetup'
import { useSettingsSite } from '@/settings/site'
import { may, useActiveTenant } from '@/tenant/context'
import { Empty, ErrorState, Loading } from '@/ui/states'

/**
 * Settings › SEO › Publish target — where a site's approved articles go.
 *
 * Settings consolidation (2026-09-15). The setup card was the top half of
 * `/seo/publish-targets`, which keeps the publishing half and shows the target
 * read-only. `TargetSetup` already collapses to a summary with *Edit* on Save
 * (SEO-UI-119), so it stands in for a `SettingRow` here.
 */
export function PublishTargetPanel(): JSX.Element {
  const brand = useBrand()
  const { membership } = useActiveTenant()
  const site = useSettingsSite()

  return (
    <div className="ar-stack">
      <div className="ar-row">
        <h2 className="ar-h2">Publish target</h2>
        <span className="ar-card__spacer" />
        <SiteChip site={site} />
      </div>

      {site.status === 'loading' ? <Loading label="Loading your sites" lines={2} /> : null}
      {site.status === 'error' && site.failure !== null ? (
        <ErrorState failure={site.failure} onRetry={site.retry} />
      ) : null}
      {site.status === 'ready' && site.site === null ? (
        <Empty
          title="No site yet"
          body={`Add your website first. Until ${brand.productName} knows where your pages live there is nowhere for an approved article to go.`}
          action={
            <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/seo/sites">
              Go to Sites
            </Link>
          }
        />
      ) : null}

      {site.site !== null ? (
        <TargetSetup
          key={site.site.id}
          siteId={site.site.id}
          domain={site.site.domain}
          configurable={may(membership, 'tenant:update')}
        />
      ) : null}
    </div>
  )
}
